import { fetchNotifications } from "../api/fetchNotifications";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "../../redux/store";
import { type AnyAction } from "../../redux/types";
import { NOTIFICATIONS_PAGE_RAW } from "~/utils/url/staticUrls";
import {
  selectIsNotificationsLoaded,
  selectIsNotificationsLoading,
  selectNewNotificationAmount,
  selectTotalNotificationAmount,
} from "~/Notifications/redux/NotificationsSelectors";
import { type NotificationData } from "~/Notifications/Messages";
import { fetchLatestNotifications } from "~/Notifications/api/fetchLatestNotifications";
import { apiMarkAsReadOrUnread } from "~/Notifications/api/apiMarkAsReadOrUnread";
import { bulkMarkAsRead } from "~/Notifications/api/bulkMarkAsRead";
import { markNotificationsForDeletion } from "~/Notifications/api/markNotificationsForDeletion";
import { apiMarkNotificationsAsInteracted } from "~/Notifications/api/apiMarkNotificationsAsInteracted";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function openNotificationsMenuThunk(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    // On Notifications Page the page initialiser will take care of loading
    if (!window.location.pathname.endsWith(NOTIFICATIONS_PAGE_RAW)) {
      if (!selectIsNotificationsLoaded(state) && !selectIsNotificationsLoading(state)) {
        dispatch(loadDataThunk(fetchLatestNotifications));
      }
    }
    dispatch(markNotificationsAsInteractedThunk());
  };
}

export function initialiseNotificationsPageThunk(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    if (!selectIsNotificationsLoaded(state) && !selectIsNotificationsLoading(state)) {
      dispatch(loadDataThunk(fetchNotifications));
    }
  };
}

function loadDataThunk(
  loadDataFn: () => Promise<{ results: NotificationData[]; totalAmount: number }>
): ThunkResult<void> {
  return async (dispatch) => {
    dispatch(startLoadingAction());

    try {
      const { results, totalAmount } = await loadDataFn();
      dispatch(finishLoadingAction(results, totalAmount));
    } catch (e) {
      dispatch(errorLoadingAction());
      console.error(e);
    }
  };
}

export function deleteNotificationsThunk(uids: string | string[]): ThunkResult<void> {
  return async (dispatch, getState) => {
    uids = Array.isArray(uids) ? uids : [uids];
    const state = getState();
    const needsFetch = selectTotalNotificationAmount(state) > 500;
    try {
      const delPromise = markNotificationsForDeletion(uids);
      if (needsFetch) {
        await delPromise;
        dispatch(loadDataThunk(fetchNotifications));
      } else {
        dispatch(deleteNotificationsAction(uids));
      }
    } catch (e) {
      console.error(e);
    }
  };
}

export function markNotificationsAsReadThunk(uids: string | string[], newVal: boolean): ThunkResult<void> {
  return (dispatch) => {
    uids = Array.isArray(uids) ? uids : [uids];
    if (newVal) {
      bulkMarkAsRead(uids);
    } else {
      uids.map(apiMarkAsReadOrUnread);
    }
    dispatch(updateReadStatusAction(uids, newVal));
  };
}

export function markNotificationsAsInteractedThunk(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const newNotificationAmount = selectNewNotificationAmount(state) ?? 0;
    if (newNotificationAmount > 0) {
      dispatch(markNotificationsInteractedAction());
      apiMarkNotificationsAsInteracted();
    }
  };
}

export const NOTIFICATIONS_INTERACT = "Notifications: see new notifications";
function markNotificationsInteractedAction() {
  return {
    type: NOTIFICATIONS_INTERACT,
  } as const;
}

export const NOTIFICATIONS_LOAD_START = "Notifications: start loading";
function startLoadingAction() {
  return {
    type: NOTIFICATIONS_LOAD_START,
  } as const;
}

export const NOTIFICATIONS_LOAD_FINISH = "Notifications: finish loading";
function finishLoadingAction(results: NotificationData[], totalAmount: number) {
  return {
    type: NOTIFICATIONS_LOAD_FINISH,
    payload: {
      results,
      totalAmount,
    },
  } as const;
}

export const NOTIFICATIONS_LOAD_ERROR = "Notifications: error during loading";
function errorLoadingAction() {
  return {
    type: NOTIFICATIONS_LOAD_ERROR,
  } as const;
}

export const NOTIFICATIONS_MARK_READ = "Notifications: mark notifications as read";
function updateReadStatusAction(uids: string[], isRead: boolean) {
  return {
    type: NOTIFICATIONS_MARK_READ,
    payload: { uids, isRead },
  } as const;
}

export const NOTIFICATIONS_DELETE = "Notifications: remove notifications";
function deleteNotificationsAction(uids: string[]) {
  return {
    type: NOTIFICATIONS_DELETE,
    payload: uids,
  } as const;
}

export type NotificationActions =
  | ReturnType<typeof markNotificationsInteractedAction>
  | ReturnType<typeof startLoadingAction>
  | ReturnType<typeof finishLoadingAction>
  | ReturnType<typeof errorLoadingAction>
  | ReturnType<typeof updateReadStatusAction>
  | ReturnType<typeof deleteNotificationsAction>;
